import React, { useEffect, useRef, useState } from 'react';
import { X, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

// One ad slot per session, reused across impressions/clicks so the backend
// can tie a click back to the impression that produced it. Exported so other
// ad surfaces (FeedView's interstitials) attribute to the same session.
export function getAdSessionId() {
  let id = sessionStorage.getItem('adSessionId');
  if (!id) {
    id = crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    sessionStorage.setItem('adSessionId', id);
  }
  return id;
}

// Fetches a random active ad and renders it either as a blocking pre-roll
// (ad_type === 'video', shown before playback starts) or a non-blocking
// banner (any other ad_type, shown alongside the video). Calls onReady once
// it's safe to reveal the actual video - immediately for banners/no-ad, or
// once the pre-roll finishes/is skipped.
export default function VideoAd({ onReady }) {
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prerollDone, setPrerollDone] = useState(false);
  const [skippable, setSkippable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const sessionId = useRef(getAdSessionId());
  const impressionSent = useRef(false);
  const readyFired = useRef(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/ads/random`)
      .then(res => res.json())
      .then(data => { if (!cancelled) setAd(data.ad || null); })
      .catch(() => { if (!cancelled) setAd(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const isPreroll = ad?.ad_type === 'video';

  const fireReady = () => {
    if (!readyFired.current) {
      readyFired.current = true;
      onReady?.();
    }
  };

  // Non-blocking cases (no ad, or a banner ad) can reveal the video right away.
  useEffect(() => {
    if (loading) return;
    if (!ad || !isPreroll) fireReady();
  }, [loading, ad, isPreroll]);

  useEffect(() => {
    if (!ad || impressionSent.current) return;
    impressionSent.current = true;
    fetch(`${API_URL}/api/ads/${ad.id}/impression`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId: sessionId.current })
    }).catch(() => {});
  }, [ad]);

  // Fallback skip timer in case the pre-roll's own video never fires 'ended'.
  useEffect(() => {
    if (!isPreroll || !ad) return;
    const timer = setTimeout(() => setSkippable(true), (ad.duration || 5) * 1000);
    return () => clearTimeout(timer);
  }, [isPreroll, ad]);

  if (loading || !ad) return null;

  const handleAdClick = () => {
    fetch(`${API_URL}/api/ads/${ad.id}/click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId: sessionId.current })
    }).catch(() => {});
    window.open(ad.click_url, '_blank', 'noopener,noreferrer');
  };

  if (isPreroll) {
    if (prerollDone) return null;
    return (
      <div className="relative bg-black rounded-lg overflow-hidden mb-4">
        <span className="absolute top-2 left-2 z-10 text-xs bg-black/70 px-2 py-1 rounded text-zinc-300">
          Advertisement
        </span>
        <video
          className="w-full"
          src={ad.media_url}
          autoPlay
          onEnded={() => { setPrerollDone(true); fireReady(); }}
        />
        <button
          onClick={handleAdClick}
          className="absolute bottom-4 left-4 flex items-center gap-1 text-sm bg-red-600 hover:bg-red-700 px-3 py-2 rounded transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          {ad.title}
        </button>
        {skippable && (
          <button
            onClick={() => { setPrerollDone(true); fireReady(); }}
            className="absolute bottom-4 right-4 text-sm bg-zinc-800/90 hover:bg-zinc-700 px-3 py-2 rounded transition-colors"
          >
            Skip Ad
          </button>
        )}
      </div>
    );
  }

  if (dismissed) return null;

  return (
    <div className="relative flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-lg p-3 pt-4 mb-4">
      <span className="absolute top-1 left-2 text-[10px] uppercase tracking-wide text-zinc-500">
        Advertisement
      </span>
      <button onClick={handleAdClick} className="flex items-center gap-3 flex-1 text-left min-w-0">
        {ad.media_url && (
          <img src={ad.media_url} alt={ad.title} className="w-20 h-12 object-cover rounded flex-shrink-0" />
        )}
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">{ad.title}</div>
          {ad.description && (
            <div className="text-xs text-zinc-400 truncate">{ad.description}</div>
          )}
        </div>
      </button>
      <button
        onClick={() => setDismissed(true)}
        className="p-1 hover:bg-zinc-800 rounded-full flex-shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
